import { useQuery } from "@apollo/client/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { toPlainDateString } from "@sa-nu/shared-utils/date";
import { removeNullProperties } from "@sa-nu/shared-utils/misc";
import type { ReactNode } from "react";
import { useState, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";

import type { FragmentType } from "../../../__generated__/gql";
import { graphql, useFragment } from "../../../__generated__/gql";
import type { AccommodationReservationProvider } from "../../../__generated__/gql/graphql";
import { AccommodationFacilityType } from "../../../__generated__/gql/graphql";
import { RESERVATION_FORM_DEFAULT_VALUES } from "../../../constants";
import {
  buildAccommodationReservationProviderInput,
  shoudShowRemoteCancelPolicy,
} from "../../../utils";

import type { SelectedAccommodationReservation } from "./reservation-calendar";
import type {
  DraftAccommodationReservationOptions,
  DraftReservationFormValues,
} from "./reservation-form";
import DraftReservationForm, {
  Query,
  reservationFormSchema,
} from "./reservation-form";

export const DRAFT_RESERVATION_DEFAULT_CAPACITY = 4;

export const OriginalAccommodationReservationFragment = graphql(`
  fragment OriginalAccommodationReservationOnDraftReservation on AccommodationReservation {
    id
    detail {
      useEvCharger
      bringDog
      numberOfDogs
    }
    ...OriginalAccommodationReservationOnDraftReservationForm
  }
`);

/**
 * 予約に必要な情報を記入するためのフォームのWrapper
 * {@link DraftReservationForm}を制御している
 *
 * @see {@link InternalDraftReservation}はAdmin予約バージョン。
 * @see /tentative-reservations/[id]/confirm は仮予約バージョン。
 */
export default function DraftReservation({
  provider,
  selected,
  originalReservationData,
  cancelFeePolicyHidden = false,
  termsAgreementNoticeHidden = false,
  additionalNotice,
  formRef,
  isRoomTypeReservation,
}: {
  provider: AccommodationReservationProvider;
  selected: SelectedAccommodationReservation;
  originalReservationData: FragmentType<
    typeof OriginalAccommodationReservationFragment
  > | null;
  cancelFeePolicyHidden?: boolean;
  termsAgreementNoticeHidden?: boolean;
  additionalNotice?: ReactNode;
  formRef?: React.RefObject<HTMLFormElement | null>;
  isRoomTypeReservation?: boolean;
}) {
  const {
    firstNight,
    lastNight,
    accommodationId,
    accommodationFacilities: facilities,
  } = selected;

  const originalReservation = useFragment(
    OriginalAccommodationReservationFragment,
    originalReservationData,
  );

  const [options, setOptions] = useState<DraftAccommodationReservationOptions>({
    bringDog: facilities.includes(AccommodationFacilityType.DogFriendly)
      ? originalReservation
        ? originalReservation.detail.bringDog
        : RESERVATION_FORM_DEFAULT_VALUES.bringDog
      : false,
    numberOfDogs: facilities.includes(AccommodationFacilityType.DogFriendly)
      ? originalReservation
        ? originalReservation.detail.bringDog
          ? originalReservation.detail.numberOfDogs
          : 0
        : RESERVATION_FORM_DEFAULT_VALUES.numberOfDogs
      : 0,
    useEvCharger: facilities.includes(AccommodationFacilityType.EvCharger)
      ? originalReservation
        ? originalReservation.detail.useEvCharger
        : RESERVATION_FORM_DEFAULT_VALUES.useEvCharger
      : false,
  });

  const defaultValues = {
    numberOfKids: 0,
    numberOfInfants: 0,
    previousLocation: "自宅",
    nextLocation: "自宅",
    capacity: DRAFT_RESERVATION_DEFAULT_CAPACITY,
    allowRoomChange: isRoomTypeReservation ? true : undefined,
    canSelectAllowRoomChange: false, // 後でuseEffectで更新される
    dogFriendlyOptions: {
      bringDog: options.bringDog,
      numberOfDogs: options.bringDog ? options.numberOfDogs : 0,
      agreeDogTerms: false,
    },
    evChargerOptions: {
      useEvCharger: options.useEvCharger,
      agreeEVChargerTerms: false,
    },
    remoteSiteCancelFeeOptions: {
      agreeRemoteCancelFeeTerms: null,
    },
    ...(originalReservation
      ? removeNullProperties(originalReservation.detail)
      : {}),
  };

  // React Hook Formの設定
  const methods = useForm<DraftReservationFormValues>({
    resolver: zodResolver(reservationFormSchema),
    defaultValues,
    mode: "onChange",
  });

  // 人数を form state から購読し、見積もりクエリにリアルタイム反映する。
  // numberOfAdults が未入力 (undefined / NaN) のときは 2 をフォールバックとして送ることで、
  // AdditionalGuestFeePolicy 紐付き master でも初期表示時に追加料金が出てしまわないようにする。
  const watchedAdults = methods.watch("numberOfAdults");
  const watchedKids = methods.watch("numberOfKids");
  const watchedInfants = methods.watch("numberOfInfants");
  const guestCounts = {
    numberOfAdults: Number.isFinite(watchedAdults) ? watchedAdults : 2,
    numberOfKids: Number.isFinite(watchedKids) ? watchedKids : 0,
    numberOfInfants: Number.isFinite(watchedInfants) ? watchedInfants : 0,
  };

  const { data, loading, previousData } = useQuery(Query, {
    variables: {
      input: {
        accommodationId: accommodationId,
        firstNight: toPlainDateString(firstNight),
        lastNight: toPlainDateString(lastNight),
        options: options,
        guestCounts,
        provider: buildAccommodationReservationProviderInput(provider),
        originalReservationIdToExcludeForAdditionalNights:
          originalReservation?.id,
      },
      provider: buildAccommodationReservationProviderInput(provider),
      lastNight: toPlainDateString(lastNight),
    },
    // 前回のデータを保持する
    notifyOnNetworkStatusChange: true,
  });

  // 現在のデータまたは前回のデータを使用
  const currentData = data || previousData;

  // capacity と canSelectAllowRoomChange を取得した時に setValue で値を更新する。
  const { setValue } = methods;
  useEffect(() => {
    const newCapacity =
      currentData?.draftAccommodationReservation?.accommodation.architectureType
        .capacity;
    if (newCapacity) {
      setValue("capacity", newCapacity);
    }

    const canSelectAllowRoomChange =
      currentData?.draftAccommodationReservation?.canSelectAllowRoomChange;
    if (canSelectAllowRoomChange !== undefined) {
      setValue("canSelectAllowRoomChange", canSelectAllowRoomChange);
    }
  }, [
    currentData?.draftAccommodationReservation?.accommodation.architectureType
      .capacity,
    currentData?.draftAccommodationReservation?.canSelectAllowRoomChange,
    setValue,
  ]);

  // 遠方キャンセルポリシーの同意チェックボックスの初期値を設定する
  // FIXME: Workaroundっぽいのできちんとした方法を考えたい・・・
  // tentative-reservationの方はデータが更新されたらRHFの `reset` を読んでデータを初期化しているので、
  // この方式に乗った方が良いのではないかと考えている。
  useEffect(() => {
    const accommodation =
      currentData?.draftAccommodationReservation?.accommodation;
    const reservation = currentData?.draftAccommodationReservation;

    if (!accommodation || !reservation) {
      setValue("remoteSiteCancelFeeOptions.agreeRemoteCancelFeeTerms", null);
      return;
    }

    const shouldShow = shoudShowRemoteCancelPolicy(accommodation, reservation);
    setValue(
      "remoteSiteCancelFeeOptions.agreeRemoteCancelFeeTerms",
      shouldShow ? false : null,
    );
  }, [
    currentData?.draftAccommodationReservation,
    currentData?.draftAccommodationReservation?.accommodation.site.isRemote,
    setValue,
  ]);

  const draftReservation = currentData?.draftAccommodationReservation;

  // 日付・サイト変更時にフォームを再マウントするためのkey
  // これにより、useEffectなしでdefaultValuesが再計算される
  const formKey = `${accommodationId}-${toPlainDateString(
    firstNight,
  )}-${toPlainDateString(lastNight)}`;

  return (
    <FormProvider key={formKey} {...methods}>
      {!draftReservation ? (
        <div
          className="py-4 text-center"
          data-testid="draft-reservation-loading"
        >
          データを取得中...
        </div>
      ) : (
        <div className="w-full" data-testid="draft-reservation-form">
          <DraftReservationForm
            provider={provider}
            reservation={draftReservation}
            updateOptions={setOptions}
            originalReservationData={originalReservation}
            formRef={formRef}
            cancelFeePolicyHidden={cancelFeePolicyHidden}
            termsAgreementNoticeHidden={termsAgreementNoticeHidden}
            additionalNotice={additionalNotice}
            loading={loading}
            isRoomTypeReservation={isRoomTypeReservation}
          />
        </div>
      )}
    </FormProvider>
  );
}
